/**
 * Skeleton Component - Loading placeholder
 *
 * Variants:
 * - default: Simple skeleton
 * - text: Text line skeleton
 * - circular: Circular skeleton (for avatars)
 * - avatar: Avatar skeleton with icon
 *
 * Animations:
 * - pulse: Pulse animation
 * - wave: Wave animation
 * - none: No animation
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';
import { User } from 'lucide-react';

const skeletonVariants = cva(
  'bg-neutral-200',
  {
    variants: {
      variant: {
        default: 'rounded',
        text: 'rounded h-4 w-full',
        circular: 'rounded-full',
        avatar: 'rounded-full flex items-center justify-center',
      },
      animation: {
        pulse: 'animate-pulse',
        wave: 'animate-shimmer',
        none: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      animation: 'pulse',
    },
  }
);

export interface SkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonVariants> {
  width?: string | number;
  height?: string | number;
  count?: number;
}

/**
 * Skeleton Component
 *
 * @example
 * ```tsx
 * <Skeleton />
 * <Skeleton width={100} height={20} />
 * <Skeleton variant="text" />
 * <Skeleton variant="circular" width={40} height={40} />
 * <Skeleton variant="avatar" size="lg" />
 * <Skeleton count={3} /> // Multiple lines
 * ```
 */
const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant, animation, width, height, count = 1, ...props }, ref) => {
  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  const content = variant === 'avatar' ? (
    <User className="h-1/2 w-1/2 text-neutral-400" />
  ) : null;

  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          ref={i === 0 ? ref : null}
          className={cn(skeletonVariants({ variant, animation }), className)}
          style={style}
          {...props}
        >
          {content}
        </div>
      ))}
    </>
  );
});

Skeleton.displayName = 'Skeleton';

export { Skeleton, skeletonVariants };
export default Skeleton;
